import { normalizeCategoryId } from './categories';

export const NOMINAL_ACCOUNTS = {
  BANK: { code: '1200', name: 'Bank / Cash', type: 'asset' },
  TRADE_DEBTORS: { code: '1300', name: 'Trade Debtors', type: 'asset' },
  FIXED_ASSETS: { code: '1500', name: 'Fixed Assets at Cost', type: 'asset' },
  VAT_CONTROL: { code: '2200', name: 'VAT Control', type: 'liability' },
  LOANS: { code: '2300', name: 'Loans', type: 'liability' },
  DIRECTOR_LOAN: { code: '2310', name: "Director's Loan Account", type: 'liability' },
  CAPITAL_INTRODUCED: { code: '3000', name: 'Capital Introduced', type: 'equity' },
  SALES: { code: '4000', name: 'Sales', type: 'income' },
  PRODUCT_SALES: { code: '4010', name: 'Product Sales', type: 'income' },
  GRANTS: { code: '4100', name: 'Grants / Government Funding', type: 'income' },
  ASSET_DISPOSALS: { code: '4200', name: 'Asset Disposal Proceeds', type: 'income' },
  COST_OF_SALES: { code: '5000', name: 'Cost of Sales / Supplies', type: 'expense' },
  RENT: { code: '7100', name: 'Rent / Lease', type: 'expense' },
  UTILITIES: { code: '7200', name: 'Utilities', type: 'expense' },
  TELEPHONE: { code: '7300', name: 'Internet / Telephone', type: 'expense' },
  STAFF: { code: '7400', name: 'Staff Costs', type: 'expense' },
  INSURANCE: { code: '7500', name: 'Insurance', type: 'expense' },
  MARKETING: { code: '7600', name: 'Marketing', type: 'expense' },
  TRAVEL: { code: '7700', name: 'Travel', type: 'expense' },
  MOTOR: { code: '7800', name: 'Motor Expenses', type: 'expense' },
  PROFESSIONAL_FEES: { code: '7900', name: 'Professional Fees', type: 'expense' },
  FINANCE_COSTS: { code: '8000', name: 'Finance Costs', type: 'expense' },
  TRAINING: { code: '8100', name: 'Training', type: 'expense' },
  REPAIRS: { code: '8200', name: 'Repairs and Maintenance', type: 'expense' },
  SOFTWARE: { code: '8300', name: 'Software / Apps', type: 'expense' },
  OFFICE: { code: '8400', name: 'Office Supplies', type: 'expense' },
  GENERAL_EXPENSES: { code: '8500', name: 'General Expenses', type: 'expense' },
  MEDICAL: { code: '8600', name: 'Medical Expenses', type: 'expense' },
  MISC: { code: '8900', name: 'Miscellaneous Expenses', type: 'expense' }
};

const CAPITAL_ASSET_CATEGORIES = new Set(['equipment', 'furniture', 'electronics', 'salon_tools', 'vehicle']);

const INCOME_CATEGORY_ACCOUNT_MAP = {
  retail: NOMINAL_ACCOUNTS.PRODUCT_SALES,
  productSales: NOMINAL_ACCOUNTS.PRODUCT_SALES,
  gov_funding: NOMINAL_ACCOUNTS.GRANTS,
  loan: NOMINAL_ACCOUNTS.LOANS,
  director_loan: NOMINAL_ACCOUNTS.DIRECTOR_LOAN,
  assetSale: NOMINAL_ACCOUNTS.ASSET_DISPOSALS
};

const EXPENSE_CATEGORY_ACCOUNT_MAP = {
  supplies: NOMINAL_ACCOUNTS.COST_OF_SALES,
  retail_stock: NOMINAL_ACCOUNTS.COST_OF_SALES,
  materials: NOMINAL_ACCOUNTS.COST_OF_SALES,
  rent: NOMINAL_ACCOUNTS.RENT,
  utilities: NOMINAL_ACCOUNTS.UTILITIES,
  internet_telephone: NOMINAL_ACCOUNTS.TELEPHONE,
  staffCosts: NOMINAL_ACCOUNTS.STAFF,
  employer_prsi: NOMINAL_ACCOUNTS.STAFF,
  employee_holiday_pay: NOMINAL_ACCOUNTS.STAFF,
  owner_pension: NOMINAL_ACCOUNTS.STAFF,
  naersa_employer_contributions: NOMINAL_ACCOUNTS.STAFF,
  naersa_employee_contributions: NOMINAL_ACCOUNTS.STAFF,
  insurance: NOMINAL_ACCOUNTS.INSURANCE,
  marketing: NOMINAL_ACCOUNTS.MARKETING,
  travel_expenses: NOMINAL_ACCOUNTS.TRAVEL,
  meals: NOMINAL_ACCOUNTS.TRAVEL,
  tips: NOMINAL_ACCOUNTS.TRAVEL,
  vehicle_fuel: NOMINAL_ACCOUNTS.MOTOR,
  vehicle_repairs_licensing: NOMINAL_ACCOUNTS.MOTOR,
  accounting: NOMINAL_ACCOUNTS.PROFESSIONAL_FEES,
  legal: NOMINAL_ACCOUNTS.PROFESSIONAL_FEES,
  interest: NOMINAL_ACCOUNTS.FINANCE_COSTS,
  bank_charges: NOMINAL_ACCOUNTS.FINANCE_COSTS,
  loan_repayment: NOMINAL_ACCOUNTS.LOANS,
  training: NOMINAL_ACCOUNTS.TRAINING,
  repairs: NOMINAL_ACCOUNTS.REPAIRS,
  cleaning: NOMINAL_ACCOUNTS.REPAIRS,
  maintenance: NOMINAL_ACCOUNTS.REPAIRS,
  software: NOMINAL_ACCOUNTS.SOFTWARE,
  office: NOMINAL_ACCOUNTS.OFFICE,
  medical_expenses: NOMINAL_ACCOUNTS.MEDICAL,
  general_expenses: NOMINAL_ACCOUNTS.GENERAL_EXPENSES,
  misc: NOMINAL_ACCOUNTS.MISC
};

const roundCurrency = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : 0;
};

const getTransactionDate = (transaction) => {
  const rawDate = transaction?.date || transaction?.purchaseDate || transaction?.invoiceDate || transaction?.createdAt;
  const parsed = rawDate ? new Date(rawDate) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
};

const getTransactionYear = (transaction) => getTransactionDate(transaction)?.getFullYear() || null;

export const isCapitalAssetCategory = (category) => CAPITAL_ASSET_CATEGORIES.has(normalizeCategoryId(category));

export const getNominalAccountForTransaction = (transaction, options = {}) => {
  const category = normalizeCategoryId(transaction?.category);
  const type = transaction?.type === 'income' ? 'income' : 'expenses';

  if (type === 'income') {
    return INCOME_CATEGORY_ACCOUNT_MAP[transaction?.category] ||
      INCOME_CATEGORY_ACCOUNT_MAP[category] ||
      NOMINAL_ACCOUNTS.SALES;
  }

  if (typeof options.isCapitalExpense === 'function' && options.isCapitalExpense(category)) {
    return NOMINAL_ACCOUNTS.FIXED_ASSETS;
  }

  if (isCapitalAssetCategory(category)) {
    return NOMINAL_ACCOUNTS.FIXED_ASSETS;
  }

  return EXPENSE_CATEGORY_ACCOUNT_MAP[transaction?.category] ||
    EXPENSE_CATEGORY_ACCOUNT_MAP[category] ||
    NOMINAL_ACCOUNTS.GENERAL_EXPENSES;
};

const getVatAmount = (transaction, options = {}) => {
  if (typeof options.getTransactionVatAmount === 'function') {
    return roundCurrency(options.getTransactionVatAmount(transaction));
  }
  return roundCurrency(transaction?.vatAmount);
};

const createLedgerLine = ({ transaction, account, debit = 0, credit = 0, memo = '' }) => ({
  transactionId: transaction?.id || '',
  date: transaction?.date || '',
  description: transaction?.description || transaction?.supplier || transaction?.clientName || '',
  category: transaction?.category || '',
  accountCode: account.code,
  accountName: account.name,
  accountType: account.type,
  debit: roundCurrency(debit),
  credit: roundCurrency(credit),
  memo
});

export const buildNominalLedgerEntries = (transactions = [], options = {}) => {
  const normalizedTransactions = Array.isArray(transactions) ? transactions : [];
  const taxYear = Number(options.taxYear) || null;
  const filteredTransactions = taxYear
    ? normalizedTransactions.filter((transaction) => getTransactionYear(transaction) === taxYear)
    : normalizedTransactions;

  return filteredTransactions.flatMap((transaction) => {
    const type = transaction?.type === 'income' ? 'income' : 'expenses';
    const grossAmount = roundCurrency(transaction?.amount);
    if (!grossAmount) return [];

    const vatAmount = Math.max(0, getVatAmount(transaction, options));
    const netAmount = roundCurrency(Math.max(0, Math.abs(grossAmount) - vatAmount));
    const account = getNominalAccountForTransaction(transaction, options);

    if (type === 'income') {
      const creditAccount = account;
      return [
        createLedgerLine({
          transaction,
          account: NOMINAL_ACCOUNTS.BANK,
          debit: Math.abs(grossAmount),
          memo: 'Income receipt'
        }),
        createLedgerLine({
          transaction,
          account: creditAccount,
          credit: netAmount,
          memo: creditAccount.code === NOMINAL_ACCOUNTS.LOANS.code ? 'Loan received' : 'Income net of VAT'
        }),
        ...(vatAmount > 0 ? [createLedgerLine({
          transaction,
          account: NOMINAL_ACCOUNTS.VAT_CONTROL,
          credit: vatAmount,
          memo: 'Output VAT'
        })] : [])
      ];
    }

    const debitAccount = account;
    return [
      createLedgerLine({
        transaction,
        account: debitAccount,
        debit: netAmount,
        memo: debitAccount.code === NOMINAL_ACCOUNTS.FIXED_ASSETS.code ? 'Capital asset purchase' : 'Expense net of VAT'
      }),
      ...(vatAmount > 0 ? [createLedgerLine({
        transaction,
        account: NOMINAL_ACCOUNTS.VAT_CONTROL,
        debit: vatAmount,
        memo: 'Input VAT'
      })] : []),
      createLedgerLine({
        transaction,
        account: NOMINAL_ACCOUNTS.BANK,
        credit: Math.abs(grossAmount),
        memo: 'Expense payment'
      })
    ];
  });
};

export const buildManualJournalLedgerEntries = (journalEntries = [], options = {}) => {
  const normalizedJournalEntries = Array.isArray(journalEntries) ? journalEntries : [];
  const taxYear = Number(options.taxYear) || null;

  return normalizedJournalEntries
    .filter((journal) => {
      if (!journal || typeof journal !== 'object') return false;
      if (!taxYear) return true;
      return getTransactionYear(journal) === taxYear;
    })
    .flatMap((journal) => {
      const lines = Array.isArray(journal.lines) ? journal.lines : [];
      const totalDebits = roundCurrency(lines.reduce((sum, line) => sum + Math.max(0, roundCurrency(line?.debit)), 0));
      const totalCredits = roundCurrency(lines.reduce((sum, line) => sum + Math.max(0, roundCurrency(line?.credit)), 0));
      if (Math.abs(totalDebits - totalCredits) >= 0.01) return [];

      return lines
        .map((line, index) => {
          const debit = Math.max(0, roundCurrency(line?.debit));
          const credit = Math.max(0, roundCurrency(line?.credit));
          if (!debit && !credit) return null;
          const account = {
            code: String(line?.accountCode || '').trim(),
            name: String(line?.accountName || '').trim(),
            type: String(line?.accountType || '').trim()
          };
          if (!account.code || !account.name || !account.type) return null;
          return {
            transactionId: journal.id || '',
            date: journal.date || '',
            description: journal.description || journal.reference || 'Manual journal',
            category: 'manual_journal',
            accountCode: account.code,
            accountName: account.name,
            accountType: account.type,
            debit,
            credit,
            memo: line?.memo || journal.reference || `Journal line ${index + 1}`
          };
        })
        .filter(Boolean);
    });
};

export const buildTrialBalance = (transactions = [], options = {}) => {
  const ledgerEntries = [
    ...buildNominalLedgerEntries(transactions, options),
    ...buildManualJournalLedgerEntries(options.journalEntries, options)
  ];
  const accountMap = new Map();

  ledgerEntries.forEach((entry) => {
    const existing = accountMap.get(entry.accountCode) || {
      accountCode: entry.accountCode,
      accountName: entry.accountName,
      accountType: entry.accountType,
      debit: 0,
      credit: 0
    };
    existing.debit = roundCurrency(existing.debit + entry.debit);
    existing.credit = roundCurrency(existing.credit + entry.credit);
    accountMap.set(entry.accountCode, existing);
  });

  const rows = Array.from(accountMap.values())
    .map((row) => {
      const net = roundCurrency(row.debit - row.credit);
      return {
        ...row,
        debit: net > 0 ? net : 0,
        credit: net < 0 ? Math.abs(net) : 0
      };
    })
    .filter((row) => row.debit !== 0 || row.credit !== 0)
    .sort((a, b) => a.accountCode.localeCompare(b.accountCode));

  const totalDebits = roundCurrency(rows.reduce((sum, row) => sum + row.debit, 0));
  const totalCredits = roundCurrency(rows.reduce((sum, row) => sum + row.credit, 0));

  return {
    rows,
    totalDebits,
    totalCredits,
    difference: roundCurrency(totalDebits - totalCredits),
    ledgerEntries
  };
};

const createProfitAndLossLine = (row, amount) => ({
  accountCode: row.accountCode,
  accountName: row.accountName,
  amount: roundCurrency(amount)
});

const sortProfitAndLossRows = (rows = []) => (
  [...rows]
    .filter((row) => roundCurrency(row.amount) !== 0)
    .sort((a, b) => a.accountCode.localeCompare(b.accountCode))
);

const sumProfitAndLossRows = (rows = []) => roundCurrency(
  rows.reduce((sum, row) => sum + roundCurrency(row.amount), 0)
);

export const buildProfitAndLossFromTrialBalance = (trialBalance = {}) => {
  const incomeRows = [];
  const expenseRows = [];

  (Array.isArray(trialBalance.rows) ? trialBalance.rows : []).forEach((row) => {
    const debit = roundCurrency(row.debit);
    const credit = roundCurrency(row.credit);

    if (row.accountType === 'income') {
      incomeRows.push(createProfitAndLossLine(row, credit - debit));
      return;
    }

    if (row.accountType === 'expense') {
      expenseRows.push(createProfitAndLossLine(row, debit - credit));
    }
  });

  const sections = {
    income: sortProfitAndLossRows(incomeRows),
    expenses: sortProfitAndLossRows(expenseRows)
  };
  const totalIncome = sumProfitAndLossRows(sections.income);
  const totalExpenses = sumProfitAndLossRows(sections.expenses);

  return {
    sections,
    totals: {
      income: totalIncome,
      expenses: totalExpenses,
      netProfit: roundCurrency(totalIncome - totalExpenses)
    }
  };
};

export const buildProfitAndLoss = (transactions = [], options = {}) => (
  buildProfitAndLossFromTrialBalance(buildTrialBalance(transactions, options))
);

const createBalanceSheetLine = (row, amount) => ({
  accountCode: row.accountCode,
  accountName: row.accountName,
  amount: roundCurrency(amount)
});

const sortBalanceSheetRows = (rows = []) => (
  [...rows]
    .filter((row) => roundCurrency(row.amount) !== 0)
    .sort((a, b) => a.accountCode.localeCompare(b.accountCode))
);

const sumBalanceSheetRows = (rows = []) => roundCurrency(
  rows.reduce((sum, row) => sum + roundCurrency(row.amount), 0)
);

const normalizeBankAccountsForReport = (bankAccounts = []) => (
  (Array.isArray(bankAccounts) ? bankAccounts : [])
    .filter((account) => account && typeof account === 'object')
    .map((account, index) => ({
      accountCode: account.nominalCode || `12${String(index + 1).padStart(2, '0')}`,
      accountName: account.name || `Bank Account ${index + 1}`,
      amount: roundCurrency(account.balance),
      accountType: account.accountType || 'current'
    }))
    .filter((account) => account.amount !== 0)
);

export const buildBalanceSheetFromTrialBalance = (trialBalance = {}, options = {}) => {
  const manualBankAccounts = normalizeBankAccountsForReport(options.bankAccounts);
  const hasManualBankAccounts = manualBankAccounts.length > 0;
  const rows = (Array.isArray(trialBalance.rows) ? trialBalance.rows : [])
    .filter((row) => !(hasManualBankAccounts && row.accountCode === NOMINAL_ACCOUNTS.BANK.code));
  const nonCurrentAssets = [];
  const currentAssets = [];
  const currentLiabilities = [];
  const nonCurrentLiabilities = [];
  const equityRows = [];
  let retainedProfit = 0;

  rows.forEach((row) => {
    const debit = roundCurrency(row.debit);
    const credit = roundCurrency(row.credit);
    const netDebit = roundCurrency(debit - credit);
    const netCredit = roundCurrency(credit - debit);

    if (row.accountType === 'income') {
      retainedProfit = roundCurrency(retainedProfit + netCredit);
      return;
    }

    if (row.accountType === 'expense') {
      retainedProfit = roundCurrency(retainedProfit - netDebit);
      return;
    }

    if (row.accountType === 'asset') {
      if (netDebit >= 0) {
        const target = row.accountCode === NOMINAL_ACCOUNTS.FIXED_ASSETS.code ? nonCurrentAssets : currentAssets;
        target.push(createBalanceSheetLine(row, netDebit));
      } else {
        currentLiabilities.push(createBalanceSheetLine({
          ...row,
          accountName: `${row.accountName} Overdrawn`
        }, Math.abs(netDebit)));
      }
      return;
    }

    if (row.accountType === 'liability') {
      if (netCredit >= 0) {
        const target = row.accountCode === NOMINAL_ACCOUNTS.LOANS.code ? nonCurrentLiabilities : currentLiabilities;
        target.push(createBalanceSheetLine(row, netCredit));
      } else {
        currentAssets.push(createBalanceSheetLine({
          ...row,
          accountName: `${row.accountName} Recoverable`
        }, Math.abs(netCredit)));
      }
      return;
    }

    if (row.accountType === 'equity') {
      equityRows.push(createBalanceSheetLine(row, netCredit - netDebit));
    }
  });

  manualBankAccounts.forEach((account) => {
    const row = {
      accountCode: account.accountCode,
      accountName: account.accountName,
      accountType: 'asset'
    };
    if (account.amount >= 0) {
      currentAssets.push(createBalanceSheetLine(row, account.amount));
    } else {
      currentLiabilities.push(createBalanceSheetLine({
        ...row,
        accountName: `${account.accountName} Overdrawn`
      }, Math.abs(account.amount)));
    }
  });

  const retainedProfitLine = {
    accountCode: '3900',
    accountName: retainedProfit >= 0 ? 'Current Year Profit' : 'Current Year Loss',
    amount: retainedProfit
  };
  equityRows.push(retainedProfitLine);

  const sections = {
    nonCurrentAssets: sortBalanceSheetRows(nonCurrentAssets),
    currentAssets: sortBalanceSheetRows(currentAssets),
    currentLiabilities: sortBalanceSheetRows(currentLiabilities),
    nonCurrentLiabilities: sortBalanceSheetRows(nonCurrentLiabilities),
    equity: sortBalanceSheetRows(equityRows)
  };

  const totalAssets = roundCurrency(
    sumBalanceSheetRows(sections.nonCurrentAssets) +
    sumBalanceSheetRows(sections.currentAssets)
  );
  const totalLiabilities = roundCurrency(
    sumBalanceSheetRows(sections.currentLiabilities) +
    sumBalanceSheetRows(sections.nonCurrentLiabilities)
  );
  const totalEquity = sumBalanceSheetRows(sections.equity);
  const totalLiabilitiesAndEquity = roundCurrency(totalLiabilities + totalEquity);

  return {
    sections,
    totals: {
      nonCurrentAssets: sumBalanceSheetRows(sections.nonCurrentAssets),
      currentAssets: sumBalanceSheetRows(sections.currentAssets),
      currentLiabilities: sumBalanceSheetRows(sections.currentLiabilities),
      nonCurrentLiabilities: sumBalanceSheetRows(sections.nonCurrentLiabilities),
      equity: totalEquity,
      assets: totalAssets,
      liabilities: totalLiabilities,
      liabilitiesAndEquity: totalLiabilitiesAndEquity,
      difference: roundCurrency(totalAssets - totalLiabilitiesAndEquity)
    }
  };
};

export const buildBalanceSheet = (transactions = [], options = {}) => (
  buildBalanceSheetFromTrialBalance(buildTrialBalance(transactions, options), options)
);

export const balanceSheetRowsToCsv = (balanceSheet = {}) => {
  const escapeCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const lines = [['Section', 'Account Code', 'Account Name', 'Amount']];
  const sectionLabels = {
    nonCurrentAssets: 'Non-Current Assets',
    currentAssets: 'Current Assets',
    currentLiabilities: 'Current Liabilities',
    nonCurrentLiabilities: 'Non-Current Liabilities',
    equity: 'Capital and Reserves'
  };

  Object.entries(sectionLabels).forEach(([sectionKey, label]) => {
    const rows = balanceSheet?.sections?.[sectionKey] || [];
    rows.forEach((row) => {
      lines.push([label, row.accountCode, row.accountName, row.amount.toFixed(2)]);
    });
  });

  lines.push(['Total Assets', '', '', roundCurrency(balanceSheet?.totals?.assets).toFixed(2)]);
  lines.push(['Total Liabilities', '', '', roundCurrency(balanceSheet?.totals?.liabilities).toFixed(2)]);
  lines.push(['Total Capital and Reserves', '', '', roundCurrency(balanceSheet?.totals?.equity).toFixed(2)]);
  lines.push(['Check Difference', '', '', roundCurrency(balanceSheet?.totals?.difference).toFixed(2)]);

  return lines.map((line) => line.map(escapeCell).join(',')).join('\n');
};

export const profitAndLossRowsToCsv = (profitAndLoss = {}) => {
  const escapeCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const lines = [['Section', 'Account Code', 'Account Name', 'Amount']];
  const sectionLabels = {
    income: 'Income',
    expenses: 'Expenses'
  };

  Object.entries(sectionLabels).forEach(([sectionKey, label]) => {
    const rows = profitAndLoss?.sections?.[sectionKey] || [];
    rows.forEach((row) => {
      lines.push([label, row.accountCode, row.accountName, row.amount.toFixed(2)]);
    });
  });

  lines.push(['Total Income', '', '', roundCurrency(profitAndLoss?.totals?.income).toFixed(2)]);
  lines.push(['Total Expenses', '', '', roundCurrency(profitAndLoss?.totals?.expenses).toFixed(2)]);
  lines.push(['Net Profit', '', '', roundCurrency(profitAndLoss?.totals?.netProfit).toFixed(2)]);

  return lines.map((line) => line.map(escapeCell).join(',')).join('\n');
};

export const buildTaxComputationSummary = ({
  profitAndLoss = {},
  assetRegister = {},
  taxSummary = {},
  taxYear,
  businessStructure = '',
  businessType = 'sole_trader',
  countryId = ''
} = {}) => {
  const isLimitedCompany = businessType === 'limited_company' || taxSummary.taxType === 'corporation_tax';
  const fallbackTaxLabel = isLimitedCompany ? 'Corporation Tax' : 'Income Tax';
  const fallbackTaxType = isLimitedCompany ? 'corporation_tax' : 'income_tax';
  const fallbackAccountingProfit = roundCurrency(profitAndLoss?.totals?.netProfit);
  const fallbackBookDepreciationAddBack = roundCurrency(assetRegister?.totals?.annualDepreciation);
  const fallbackCapitalAllowances = roundCurrency(assetRegister?.totals?.capitalAllowanceForYear);
  const hasComputedTaxSummary = Boolean(
    roundCurrency(taxSummary.accountingProfitAfterDepreciation) ||
    roundCurrency(taxSummary.taxAdjustedProfit) ||
    roundCurrency(taxSummary.taxableProfit) ||
    roundCurrency(taxSummary.totalTaxLiability) ||
    roundCurrency(taxSummary.corporationTax) ||
    roundCurrency(taxSummary.incomeTax)
  );

  const accountingProfit = hasComputedTaxSummary
    ? roundCurrency(taxSummary.accountingProfitAfterDepreciation ?? fallbackAccountingProfit)
    : fallbackAccountingProfit;
  const bookDepreciationAddBack = hasComputedTaxSummary
    ? roundCurrency(taxSummary.bookDepreciationAddBack ?? fallbackBookDepreciationAddBack)
    : fallbackBookDepreciationAddBack;
  const capitalAllowances = hasComputedTaxSummary
    ? roundCurrency(taxSummary.capitalAllowances ?? fallbackCapitalAllowances)
    : fallbackCapitalAllowances;
  const fallbackTaxableProfit = roundCurrency(accountingProfit + bookDepreciationAddBack - capitalAllowances);
  const taxableProfit = hasComputedTaxSummary
    ? roundCurrency(taxSummary.taxAdjustedProfit ?? taxSummary.taxableProfit ?? fallbackTaxableProfit)
    : fallbackTaxableProfit;
  const fallbackCorporationTaxRate = String(countryId || '').toLowerCase() === 'ie' ? 0.125 : 0;
  const fallbackTaxLiability = isLimitedCompany
    ? roundCurrency(Math.max(0, taxableProfit) * fallbackCorporationTaxRate)
    : 0;
  const totalTaxLiability = hasComputedTaxSummary
    ? roundCurrency(taxSummary.totalTaxLiability ?? taxSummary.corporationTax ?? taxSummary.incomeTax ?? fallbackTaxLiability)
    : fallbackTaxLiability;
  const vatDue = roundCurrency(taxSummary.vatDue);

  return {
    taxYear: taxYear || taxSummary.taxYear || '',
    taxType: isLimitedCompany ? 'corporation_tax' : (taxSummary.taxType || fallbackTaxType),
    taxLabel: isLimitedCompany ? 'Corporation Tax' : (taxSummary.taxLabel || fallbackTaxLabel),
    businessStructure,
    rows: [
      { label: 'Accounting profit after book depreciation', amount: accountingProfit },
      { label: 'Add back book depreciation', amount: bookDepreciationAddBack },
      { label: 'Less capital allowances', amount: -Math.abs(capitalAllowances) },
      { label: 'Tax adjusted profit', amount: taxableProfit },
      { label: isLimitedCompany ? 'Corporation Tax' : (taxSummary.taxLabel || 'Estimated tax liability'), amount: totalTaxLiability }
    ],
    totals: {
      accountingProfit,
      bookDepreciationAddBack,
      capitalAllowances,
      taxableProfit,
      totalTaxLiability,
      incomeTax: roundCurrency(taxSummary.incomeTax),
      corporationTax: roundCurrency(taxSummary.corporationTax),
      usc: roundCurrency(taxSummary.usc),
      prsi: roundCurrency(taxSummary.prsi),
      vatCollected: roundCurrency(taxSummary.vatCollected),
      vatPaid: roundCurrency(taxSummary.vatPaid),
      vatDue,
      profitAfterTax: roundCurrency(taxSummary.profitAfterTax)
    },
    capitalAllowanceRows: Array.isArray(taxSummary.capitalAllowanceRows)
      ? taxSummary.capitalAllowanceRows
      : []
  };
};

export const taxComputationRowsToCsv = (taxComputation = {}) => {
  const escapeCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const rows = [
    ['Section', 'Label', 'Amount'],
    ['Summary', 'Tax Year', taxComputation.taxYear || ''],
    ['Summary', 'Business Structure', taxComputation.businessStructure || ''],
    ['Summary', 'Tax Type', taxComputation.taxLabel || '']
  ];

  (Array.isArray(taxComputation.rows) ? taxComputation.rows : []).forEach((row) => {
    rows.push(['Tax Computation', row.label, roundCurrency(row.amount).toFixed(2)]);
  });

  (Array.isArray(taxComputation.capitalAllowanceRows) ? taxComputation.capitalAllowanceRows : [])
    .filter((row) => (Number(row.bookDepreciation) || 0) !== 0 || (Number(row.capitalAllowance) || 0) !== 0)
    .forEach((row) => {
      rows.push([
        'Capital Allowances',
        row.description || row.id || '',
        roundCurrency(row.capitalAllowance).toFixed(2)
      ]);
    });

  return rows.map((line) => line.map(escapeCell).join(',')).join('\n');
};

const getAssetCost = (transaction) => {
  const amount = Number(
    transaction?.expenseTotalAmount ??
    transaction?.billTotalAmount ??
    transaction?.invoiceTotalAmount ??
    transaction?.originalAmount ??
    transaction?.amount
  );
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
};

export const buildAssetRegister = (transactions = [], options = {}) => {
  const taxYear = Number(options.taxYear) || new Date().getFullYear();
  const annualCapitalAllowanceRate = Number(options.annualCapitalAllowanceRate) || 0.125;
  const capitalAllowanceYears = Number(options.capitalAllowanceYears) || 8;
  const sourceTransactions = Array.isArray(transactions) ? transactions : [];

  const rows = sourceTransactions
    .filter((transaction) => {
      const category = normalizeCategoryId(transaction?.category);
      return typeof options.isCapitalExpense === 'function'
        ? options.isCapitalExpense(category)
        : isCapitalAssetCategory(category);
    })
    .map((transaction) => {
      const cost = roundCurrency(getAssetCost(transaction));
      if (!cost) return null;

      const purchaseDate = transaction?.date || transaction?.purchaseDate || '';
      const purchaseYear = getTransactionYear(transaction) || taxYear;
      const depreciationYears = Math.max(1, Number(transaction?.depreciationYears) || 8);
      const assetStatus = transaction?.assetStatus || 'active';
      const disposalDate = transaction?.disposalDate || '';
      const disposalAmount = roundCurrency(transaction?.disposalAmount || 0);
      const disposalYear = disposalDate ? getTransactionYear({ date: disposalDate }) : null;
      const yearsElapsed = Math.max(0, taxYear - purchaseYear);
      const yearsForDepreciation = assetStatus === 'active'
        ? yearsElapsed
        : Math.max(0, Number(transaction?.yearsAtDisposal) || (disposalYear ? disposalYear - purchaseYear : yearsElapsed));
      const annualDepreciation = roundCurrency(transaction?.annualDepreciation || (cost / depreciationYears));
      const accumulatedDepreciation = roundCurrency(
        transaction?.accumulatedDepreciation !== undefined
          ? transaction.accumulatedDepreciation
          : Math.min(cost, annualDepreciation * yearsForDepreciation)
      );
      const netBookValue = roundCurrency(
        transaction?.currentValue !== undefined
          ? transaction.currentValue
          : Math.max(0, cost - accumulatedDepreciation)
      );
      const inCapitalAllowancePeriod =
        taxYear >= purchaseYear &&
        taxYear < purchaseYear + capitalAllowanceYears &&
        !(disposalYear && disposalYear < taxYear);
      const capitalAllowanceForYear = roundCurrency(inCapitalAllowancePeriod ? cost * annualCapitalAllowanceRate : 0);
      const gainOrLossOnDisposal = disposalDate
        ? roundCurrency(disposalAmount - netBookValue)
        : 0;

      return {
        id: transaction.id || `${transaction.description || transaction.category}-${purchaseDate}-${cost}`,
        description: transaction.description || transaction.category || 'Capital asset',
        category: normalizeCategoryId(transaction.category || ''),
        purchaseDate,
        purchaseYear,
        cost,
        depreciationYears,
        annualDepreciation,
        accumulatedDepreciation,
        netBookValue,
        status: assetStatus,
        disposalDate,
        disposalAmount,
        disposalReason: transaction.disposalReason || '',
        gainOrLossOnDisposal,
        capitalAllowanceForYear
      };
    })
    .filter(Boolean)
    .sort((a, b) => String(a.purchaseDate || '').localeCompare(String(b.purchaseDate || '')));

  return {
    rows,
    totals: {
      cost: roundCurrency(rows.reduce((sum, row) => sum + row.cost, 0)),
      annualDepreciation: roundCurrency(rows.reduce((sum, row) => sum + row.annualDepreciation, 0)),
      accumulatedDepreciation: roundCurrency(rows.reduce((sum, row) => sum + row.accumulatedDepreciation, 0)),
      netBookValue: roundCurrency(rows.reduce((sum, row) => sum + row.netBookValue, 0)),
      capitalAllowanceForYear: roundCurrency(rows.reduce((sum, row) => sum + row.capitalAllowanceForYear, 0)),
      disposalProceeds: roundCurrency(rows.reduce((sum, row) => sum + row.disposalAmount, 0)),
      gainOrLossOnDisposal: roundCurrency(rows.reduce((sum, row) => sum + row.gainOrLossOnDisposal, 0))
    }
  };
};

export const assetRegisterRowsToCsv = (rows = []) => {
  const escapeCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const header = [
    'Asset',
    'Category',
    'Purchase Date',
    'Cost',
    'Depreciation Years',
    'Annual Depreciation',
    'Accumulated Depreciation',
    'Net Book Value',
    'Status',
    'Disposal Date',
    'Disposal Proceeds',
    'Gain / Loss',
    'Capital Allowance'
  ];
  const body = (Array.isArray(rows) ? rows : []).map((row) => [
    row.description,
    row.category,
    row.purchaseDate,
    row.cost.toFixed(2),
    row.depreciationYears,
    row.annualDepreciation.toFixed(2),
    row.accumulatedDepreciation.toFixed(2),
    row.netBookValue.toFixed(2),
    row.status,
    row.disposalDate,
    row.disposalAmount.toFixed(2),
    row.gainOrLossOnDisposal.toFixed(2),
    row.capitalAllowanceForYear.toFixed(2)
  ]);
  return [header, ...body].map((line) => line.map(escapeCell).join(',')).join('\n');
};

export const trialBalanceRowsToCsv = (rows = []) => {
  const escapeCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const header = ['Account Code', 'Account Name', 'Type', 'Debit', 'Credit'];
  const body = (Array.isArray(rows) ? rows : []).map((row) => [
    row.accountCode,
    row.accountName,
    row.accountType,
    row.debit.toFixed(2),
    row.credit.toFixed(2)
  ]);
  return [header, ...body].map((line) => line.map(escapeCell).join(',')).join('\n');
};
